/**
 * Sample ceramic-lot settlement model.
 *
 * FIFO default: oldest active hedge fills first. Leftover ounces from a lot
 * split onto the next hedge (per metal). Our ounces = invoice/catalog assays.
 * Partner settlement is ounces per lot (a settlement may list many lots).
 */

export const METALS = ["pt", "pd", "rh"];

export function zeroOz() {
  return { pt: 0, pd: 0, rh: 0 };
}

export function addOz(a, b) {
  return {
    pt: (a?.pt || 0) + (b?.pt || 0),
    pd: (a?.pd || 0) + (b?.pd || 0),
    rh: (a?.rh || 0) + (b?.rh || 0),
  };
}

export function roundOz(n, d = 2) {
  if (n == null || Number.isNaN(Number(n))) return null;
  return Number(Number(n).toFixed(d));
}

export function mapOz(oz, fn) {
  return {
    pt: fn(oz.pt || 0),
    pd: fn(oz.pd || 0),
    rh: fn(oz.rh || 0),
  };
}

export function ozValue(oz, prices) {
  if (!oz || !prices) return 0;
  return (oz.pt || 0) * prices.pt + (oz.pd || 0) * prices.pd + (oz.rh || 0) * prices.rh;
}

function sumConverters(converters) {
  return (converters || []).reduce((acc, c) => addOz(acc, c), zeroOz());
}

function withInvoiceTotals(invoices) {
  return invoices.map((inv) => {
    const oz = sumConverters(inv.converters);
    return { ...inv, ...oz };
  });
}

function buildLot(meta, invoices) {
  const withTotals = withInvoiceTotals(invoices);
  const ourOz = withTotals.reduce((acc, inv) => addOz(acc, inv), zeroOz());
  return {
    ...meta,
    invoices: withTotals,
    ourOz: mapOz(ourOz, (n) => roundOz(n)),
    invoiceCount: withTotals.length,
  };
}

export const SAMPLE_HEDGES = [
  {
    id: "H-0971",
    partner: "Ford",
    status: "ACTIVE",
    date: "2026-02-10",
    committed: { pt: 30, pd: 80, rh: 6 },
    prices: { pt: 960, pd: 1010, rh: 5100 },
  },
  {
    id: "H-1001",
    partner: "BASF",
    status: "ACTIVE",
    date: "2026-04-20",
    committed: { pt: 80, pd: 220, rh: 16 },
    prices: { pt: 980, pd: 980, rh: 4850 },
  },
  {
    id: "H-1002",
    partner: "Tesla",
    status: "ACTIVE",
    date: "2026-06-15",
    committed: { pt: 55, pd: 150, rh: 12 },
    prices: { pt: 975, pd: 990, rh: 4920 },
  },
];

export const SAMPLE_LOTS = [
  buildLot(
    {
      lotId: "CER-2026-0129",
      lotType: "ceramic",
      openedDate: "2026-04-12",
      closedDate: "2026-05-01",
      status: "variance",
    },
    [
      {
        loadId: "INV-8501",
        company: "Midwest CAT",
        date: "2026-04-15",
        converters: [
          { oem: "12600620", type: "CERAMIC", qty: 18, ptAssay: 1280, pdAssay: 740, rhAssay: 92, pt: 4.10, pd: 8.20, rh: 0.72 },
          { oem: "GM-4012", type: "CERAMIC", qty: 12, ptAssay: 980, pdAssay: 1100, rhAssay: 70, pt: 2.10, pd: 6.90, rh: 0.48 },
        ],
      },
      {
        loadId: "INV-8520",
        company: "Midwest CAT",
        date: "2026-04-18",
        converters: [
          { oem: "12600620", type: "CERAMIC", qty: 16, ptAssay: 1280, pdAssay: 740, rhAssay: 92, pt: 3.80, pd: 7.40, rh: 0.64 },
          { oem: "HONDA-44", type: "CERAMIC", qty: 10, ptAssay: 860, pdAssay: 920, rhAssay: 55, pt: 2.00, pd: 6.60, rh: 0.36 },
        ],
      },
      {
        loadId: "INV-8544",
        company: "Prairie Metals",
        date: "2026-04-22",
        converters: [
          { oem: "12600620", type: "CERAMIC", qty: 22, ptAssay: 1280, pdAssay: 740, rhAssay: 92, pt: 5.40, pd: 9.10, rh: 0.88 },
          { oem: "TOY-221", type: "CERAMIC", qty: 14, ptAssay: 910, pdAssay: 880, rhAssay: 60, pt: 2.80, pd: 7.20, rh: 0.42 },
        ],
      },
      {
        loadId: "INV-8560",
        company: "Prairie Metals",
        date: "2026-04-28",
        converters: [
          { oem: "FORD-8821", type: "CERAMIC", qty: 20, ptAssay: 1020, pdAssay: 990, rhAssay: 110, pt: 4.20, pd: 8.80, rh: 0.96 },
          { oem: "GM-4012", type: "CERAMIC", qty: 11, ptAssay: 980, pdAssay: 1100, rhAssay: 70, pt: 2.40, pd: 7.10, rh: 0.50 },
        ],
      },
    ]
  ),
  buildLot(
    {
      lotId: "CER-2026-0138",
      lotType: "ceramic",
      openedDate: "2026-05-28",
      closedDate: "2026-06-10",
      status: "settled",
    },
    [
      {
        loadId: "INV-8710",
        company: "Pacific Auto",
        date: "2026-05-30",
        converters: [
          { oem: "TOY-221", type: "CERAMIC", qty: 16, ptAssay: 910, pdAssay: 880, rhAssay: 60, pt: 3.50, pd: 9.80, rh: 0.70 },
        ],
      },
      {
        loadId: "INV-8722",
        company: "Valley Metals",
        date: "2026-06-02",
        converters: [
          { oem: "HONDA-44", type: "CERAMIC", qty: 14, ptAssay: 860, pdAssay: 920, rhAssay: 55, pt: 4.00, pd: 10.50, rh: 0.60 },
        ],
      },
    ]
  ),
  buildLot(
    {
      lotId: "CER-2026-0142",
      lotType: "ceramic",
      openedDate: "2026-06-15",
      closedDate: "2026-06-28",
      status: "variance",
    },
    [
      {
        loadId: "INV-8841",
        company: "Metro Recycling",
        date: "2026-06-18",
        converters: [
          { oem: "12600620", type: "CERAMIC", qty: 14, ptAssay: 1280, pdAssay: 740, rhAssay: 92, pt: 3.20, pd: 6.40, rh: 0.50 },
          { oem: "GM-4012", type: "CERAMIC", qty: 9, ptAssay: 980, pdAssay: 1100, rhAssay: 70, pt: 2.10, pd: 5.80, rh: 0.32 },
        ],
      },
      {
        loadId: "INV-8855",
        company: "Green Catalytics",
        date: "2026-06-20",
        converters: [
          { oem: "12600620", type: "CERAMIC", qty: 20, ptAssay: 1280, pdAssay: 740, rhAssay: 92, pt: 4.80, pd: 8.90, rh: 0.74 },
          { oem: "FORD-8821", type: "CERAMIC", qty: 8, ptAssay: 1020, pdAssay: 990, rhAssay: 110, pt: 1.90, pd: 4.20, rh: 0.38 },
        ],
      },
      {
        loadId: "INV-8870",
        company: "Summit Scrap",
        date: "2026-06-22",
        converters: [
          { oem: "TOY-221", type: "CERAMIC", qty: 11, ptAssay: 910, pdAssay: 880, rhAssay: 60, pt: 2.80, pd: 7.40, rh: 0.40 },
        ],
      },
      {
        loadId: "INV-8892",
        company: "Metro Recycling",
        date: "2026-06-25",
        converters: [
          { oem: "12600620", type: "CERAMIC", qty: 24, ptAssay: 1280, pdAssay: 740, rhAssay: 92, pt: 5.60, pd: 10.20, rh: 0.90 },
          { oem: "HONDA-44", type: "CERAMIC", qty: 7, ptAssay: 860, pdAssay: 920, rhAssay: 55, pt: 1.80, pd: 4.80, rh: 0.26 },
        ],
      },
    ]
  ),
  buildLot(
    {
      lotId: "CER-2026-0151",
      lotType: "ceramic",
      openedDate: "2026-07-02",
      closedDate: null,
      status: "open",
    },
    [
      {
        loadId: "INV-9010",
        company: "Coastal Scrap",
        date: "2026-07-05",
        converters: [
          { oem: "GM-4012", type: "CERAMIC", qty: 8, ptAssay: 980, pdAssay: 1100, rhAssay: 70, pt: 2.30, pd: 6.40, rh: 0.40 },
        ],
      },
      {
        loadId: "INV-9024",
        company: "Coastal Scrap",
        date: "2026-07-08",
        converters: [
          { oem: "TOY-221", type: "CERAMIC", qty: 10, ptAssay: 910, pdAssay: 880, rhAssay: 60, pt: 3.10, pd: 8.90, rh: 0.60 },
        ],
      },
    ]
  ),
];

/** Partner settlements: ounces are per lot, one file can list many lots. */
export const SAMPLE_SETTLEMENTS = [
  {
    id: "SET-FORD-01",
    partner: "Ford",
    date: "2026-06-28",
    lines: [{ lotId: "CER-2026-0129", pt: 24.1, pd: 58.6, rh: 4.55 }],
  },
  {
    id: "SET-BASF-01",
    partner: "BASF",
    date: "2026-07-18",
    lines: [
      { lotId: "CER-2026-0138", pt: 7.4, pd: 20.1, rh: 1.28 },
      { lotId: "CER-2026-0142", pt: 20.4, pd: 44.1, rh: 3.15 },
    ],
  },
];

const VARIANCE_PT = 0.4;
const VARIANCE_PD = 0.8;
const VARIANCE_RH = 0.15;

export function isMetalVariance(our, settled) {
  if (settled == null) return false;
  return (
    Math.abs((our.pt || 0) - (settled.pt || 0)) > VARIANCE_PT ||
    Math.abs((our.pd || 0) - (settled.pd || 0)) > VARIANCE_PD ||
    Math.abs((our.rh || 0) - (settled.rh || 0)) > VARIANCE_RH
  );
}

export function applySettlementsToLots(lots, settlements) {
  const byLot = {};
  settlements.forEach((s) => {
    (s.lines || []).forEach((line) => {
      byLot[line.lotId] = {
        settlementId: s.id,
        partner: s.partner,
        date: s.date,
        settlementOz: { pt: line.pt, pd: line.pd, rh: line.rh },
      };
    });
  });

  return lots.map((lot) => {
    const hit = byLot[lot.lotId];
    if (!hit) {
      return {
        ...lot,
        settlementOz: null,
        settlementId: null,
        settlementDate: null,
        settlementPartner: null,
        status: lot.closedDate ? "awaiting-settlement" : "open",
      };
    }
    const variance = isMetalVariance(lot.ourOz, hit.settlementOz);
    return {
      ...lot,
      settlementOz: hit.settlementOz,
      settlementId: hit.settlementId,
      settlementDate: hit.date,
      settlementPartner: hit.partner,
      status: variance ? "variance" : "settled",
    };
  });
}

/**
 * Split-ounce FIFO: oldest hedge first. Each metal is filled independently
 * so leftover ounces of a lot roll to the next hedge.
 */
export function allocateFifo(hedges, lots) {
  const orderedHedges = [...hedges]
    .filter((h) => h.status !== "CANCELLED")
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .map((h) => ({
      ...h,
      remaining: { ...h.committed },
      filled: zeroOz(),
      splits: [],
    }));

  const orderedLots = [...lots].sort((a, b) => {
    const da = a.closedDate || a.openedDate || "";
    const db = b.closedDate || b.openedDate || "";
    return String(da).localeCompare(String(db));
  });

  const lotSplits = {};

  orderedLots.forEach((lot) => {
    lotSplits[lot.lotId] = [];
    const leftover = { ...lot.ourOz };
    orderedHedges.forEach((hedge) => {
      const take = zeroOz();
      METALS.forEach((m) => {
        const amt = Math.min(leftover[m] || 0, hedge.remaining[m] || 0);
        if (amt > 0.0001) {
          take[m] = roundOz(amt);
          leftover[m] = roundOz((leftover[m] || 0) - amt);
          hedge.remaining[m] = roundOz((hedge.remaining[m] || 0) - amt);
          hedge.filled[m] = roundOz((hedge.filled[m] || 0) + amt);
        }
      });
      const any = METALS.some((m) => (take[m] || 0) > 0);
      if (any) {
        const row = { hedgeId: hedge.id, partner: hedge.partner, oz: take, prices: hedge.prices };
        hedge.splits.push({ lotId: lot.lotId, oz: take });
        lotSplits[lot.lotId].push(row);
      }
    });
  });

  return {
    hedges: orderedHedges.map((h) => ({
      ...h,
      fillPct: {
        pt: h.committed.pt ? Math.round((h.filled.pt / h.committed.pt) * 100) : 0,
        pd: h.committed.pd ? Math.round((h.filled.pd / h.committed.pd) * 100) : 0,
        rh: h.committed.rh ? Math.round((h.filled.rh / h.committed.rh) * 100) : 0,
      },
    })),
    lotSplits,
  };
}

export function gapOz(our, settled) {
  if (!settled) return null;
  return {
    pt: roundOz((our.pt || 0) - (settled.pt || 0)),
    pd: roundOz((our.pd || 0) - (settled.pd || 0)),
    rh: roundOz((our.rh || 0) - (settled.rh || 0)),
  };
}

function share(part, total) {
  if (!total) return 0;
  return part / total;
}

/** Rank invoices in a lot by attributed metal gap (proportional to assay ounces). */
export function rankInvoices(lot) {
  const gap = gapOz(lot.ourOz, lot.settlementOz);
  return (lot.invoices || [])
    .map((inv) => {
      const attributed = gap
        ? {
            pt: roundOz((gap.pt || 0) * share(inv.pt, lot.ourOz.pt)),
            pd: roundOz((gap.pd || 0) * share(inv.pd, lot.ourOz.pd)),
            rh: roundOz((gap.rh || 0) * share(inv.rh, lot.ourOz.rh)),
          }
        : zeroOz();
      const score =
        Math.abs(attributed.pt || 0) +
        Math.abs(attributed.pd || 0) * 0.4 +
        Math.abs(attributed.rh || 0) * 3;
      return { ...inv, attributed, score };
    })
    .sort((a, b) => b.score - a.score);
}

function flattenConverters(lots) {
  const rows = [];
  lots.forEach((lot) => {
    (lot.invoices || []).forEach((inv) => {
      (inv.converters || []).forEach((c) => {
        rows.push({ lot, inv, converter: c });
      });
    });
  });
  return rows;
}

/**
 * Pinpoint converters: attribute lot-level settlement gap to converters by
 * ounce share, then boost converters that repeat across variance lots.
 */
export function rankConverters(lots, pricesByLot = {}) {
  const byOem = {};

  flattenConverters(lots).forEach(({ lot, inv, converter }) => {
    const oem = converter.oem;
    if (!byOem[oem]) {
      byOem[oem] = {
        oem,
        type: converter.type,
        ptAssay: converter.ptAssay,
        pdAssay: converter.pdAssay,
        rhAssay: converter.rhAssay,
        qty: 0,
        ourOz: zeroOz(),
        attributed: zeroOz(),
        lots: new Set(),
        varianceLots: new Set(),
        invoices: new Set(),
      };
    }
    const row = byOem[oem];
    row.qty += converter.qty || 0;
    row.ourOz = addOz(row.ourOz, converter);
    row.lots.add(lot.lotId);
    row.invoices.add(inv.loadId);
    if (lot.status === "variance") row.varianceLots.add(lot.lotId);

    const gap = gapOz(lot.ourOz, lot.settlementOz);
    if (gap) {
      row.attributed = addOz(row.attributed, {
        pt: (gap.pt || 0) * share(converter.pt, lot.ourOz.pt),
        pd: (gap.pd || 0) * share(converter.pd, lot.ourOz.pd),
        rh: (gap.rh || 0) * share(converter.rh, lot.ourOz.rh),
      });
    }
  });

  return Object.values(byOem)
    .map((row) => {
      const attributed = mapOz(row.attributed, (n) => roundOz(n));
      const ourOz = mapOz(row.ourOz, (n) => roundOz(n));
      const prices = Object.values(pricesByLot)[0] || { pt: 980, pd: 980, rh: 4850 };
      const cashImpact = ozValue(attributed, prices);
      const repeat = row.varianceLots.size;
      const score =
        Math.abs(attributed.pt) +
        Math.abs(attributed.pd) * 0.4 +
        Math.abs(attributed.rh) * 3 +
        repeat * 0.8;
      const ptMissPct = ourOz.pt ? (attributed.pt / ourOz.pt) * 100 : 0;
      const suggestion =
        Math.abs(ptMissPct) >= 4
          ? `${ptMissPct > 0 ? "Lower" : "Raise"} Pt assay ~${Math.abs(ptMissPct).toFixed(0)}%`
          : Math.abs(attributed.pd) >= 0.4
            ? `${attributed.pd > 0 ? "Lower" : "Raise"} Pd assay`
            : Math.abs(attributed.rh) >= 0.08
              ? `${attributed.rh > 0 ? "Lower" : "Raise"} Rh assay`
              : "Watch";
      return {
        ...row,
        lots: [...row.lots],
        varianceLots: [...row.varianceLots],
        invoices: [...row.invoices],
        attributed,
        ourOz,
        cashImpact: roundOz(cashImpact, 0),
        score,
        suggestion,
        confidence: repeat >= 2 ? "high" : repeat === 1 ? "medium" : "low",
      };
    })
    .sort((a, b) => b.score - a.score);
}

export function convertersForLot(lot) {
  return rankConverters([lot]);
}
